import { Controller } from "@hotwired/stimulus"

/**
 * Upscale Controller
 *
 * Manages the upscaling process UI, including:
 * - Form submission and loading states (spinner, button disable)
 * - Progress polling when upscaling is in progress
 * - Updates progress bar, image count, time remaining, and status message
 *
 * Usage: Add data-controller="upscale"
 *        data-upscale-variant-id-value="<uuid>"
 *        data-upscale-variant-status-value="<status>"
 * to the upscale card body. Add data-upscale-target="content" and
 * data-upscale-target="spinner" to form button spans.
 */
export default class extends Controller {
  static targets = [
    "content",
    "spinner",
    "progressBar",
    "statusMessage",
    "imageCount",
    "timeRemaining",
    "progressContainer",
    "progressStats"
  ]

  static values = {
    variantId: String,
    variantStatus: String
  }

  connect() {
    document.addEventListener("turbo:before-stream-render", this.handleStreamUpdate.bind(this))

    if (this.variantStatusValue === "upscaling" && this.variantIdValue) {
      this.startProgressPolling()
    }
  }

  disconnect() {
    this.stopProgressPolling()
    document.removeEventListener("turbo:before-stream-render", this.handleStreamUpdate.bind(this))
  }

  /**
   * Called on form submit - shows spinner and disables button
   */
  start(event) {
    const button = event.target.closest("form button")
    if (!button) return
    if (this.hasContentTarget && this.hasSpinnerTarget) {
      this.contentTarget.classList.add("d-none")
      this.spinnerTarget.classList.remove("d-none")
    }
    button.disabled = true
  }

  handleStreamUpdate(event) {
    const stream = event.target
    if (stream.action === "reload" && stream.getAttribute("target") === "aside-frame") {
      // Turbo will handle the reload
    }
  }

  startProgressPolling() {
    if (this.pollInterval) return
    this.pollInterval = 5000 // 5 seconds
    this.maxPolls = 600 // 50 minutes
    this.pollCount = 0
    this.schedulePoll()
  }

  stopProgressPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
    this.pollInterval = null
  }

  schedulePoll() {
    if (!this.pollInterval || this.pollCount >= this.maxPolls) return
    this.pollTimer = setTimeout(() => this.pollProgress(), this.pollInterval)
  }

  async pollProgress() {
    if (!this.variantIdValue) return
    this.pollCount++

    try {
      const response = await fetch(`/api/v2/products/${this.variantIdValue}/upscaling_progress`)
      const data = await response.json()

      if (data.status === "processing") {
        this.updateProgressUI(data)
        this.schedulePoll()
      } else if (data.status === "completed") {
        this.stopProgressPolling()
        Turbo.visit(window.location.href, { action: "replace" })
      } else {
        this.schedulePoll()
      }
    } catch {
      this.schedulePoll()
    }
  }

  updateProgressUI(data) {
    const progress = data.progress ?? 0
    const upscaledCount = data.upscaled_count ?? 0
    const totalImages = data.total_images ?? 0
    const statusMessage = data.status_message ?? "Processing images..."
    const timeRemaining = data.estimated_time_remaining ?? "—"

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`
      this.progressBarTarget.setAttribute("aria-valuenow", progress)
      this.progressBarTarget.textContent = `${progress}%`
    }
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = statusMessage
    }
    if (this.hasImageCountTarget) {
      this.imageCountTarget.textContent = `${upscaledCount} / ${totalImages}`
    }
    if (this.hasTimeRemainingTarget) {
      this.timeRemainingTarget.textContent = timeRemaining
    }
  }
}
